import { useState, useEffect } from 'react'
import { crisisApi } from '@/services/api'
import { loadSituations, type Situation } from '@/services/content'
import styles from './SOSQuickLog.module.css'

interface Props {
  techniqueUsed: string
  durationSeconds: number
  onSave: () => void
  onSkip: () => void
}

export default function SOSQuickLog({ techniqueUsed, durationSeconds, onSave, onSkip }: Props) {
  const [situations, setSituations] = useState<Situation[]>([])
  const [situationId, setSituationId] = useState('')
  const [intensity, setIntensity] = useState(3)
  const [whatHelped, setWhatHelped] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    loadSituations().then(setSituations)
  }, [])

  const handleSave = async () => {
    setSaving(true)
    try {
      await crisisApi.createLog({
        situation_id: situationId,
        intensity,
        technique_used: techniqueUsed,
        what_helped: whatHelped,
        duration_seconds: durationSeconds,
      })
      onSave()
    } catch {
      onSave()
    }
  }

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>How are things now?</h2>
      <p className={styles.subtitle}>This helps us learn what works for your child</p>

      <div className={styles.form}>
        <div className={styles.field}>
          <label className={styles.label}>What was happening?</label>
          <select
            className={styles.select}
            value={situationId}
            onChange={(e) => setSituationId(e.target.value)}
          >
            <option value="">Select a situation (optional)</option>
            {situations.map((s) => (
              <option key={s.id} value={s.id}>{s.title}</option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>How intense was it? ({intensity}/5)</label>
          <div className={styles.intensityRow}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                className={`${styles.intensityButton} ${intensity === n ? styles.intensityActive : ''}`}
                onClick={() => setIntensity(n)}
              >
                {n}
              </button>
            ))}
          </div>
          <div className={styles.intensityLabels}>
            <span>Mild</span>
            <span>Severe</span>
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>What helped? (optional)</label>
          <textarea
            className={styles.textarea}
            value={whatHelped}
            onChange={(e) => setWhatHelped(e.target.value)}
            placeholder="e.g., Deep pressure hug calmed them down..."
            rows={3}
          />
        </div>

        <div className={styles.actions}>
          <button
            className={styles.saveButton}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button className={styles.skipButton} onClick={onSkip}>
            Skip
          </button>
        </div>
      </div>
    </div>
  )
}
